import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ShieldCheck, CheckCircle } from "@phosphor-icons/react";
import { api } from "@/lib/api";

/**
 * PaymentMethodPicker
 * -------------------
 * Reusable provider picker. Reads the merchant's advertised gateways from
 * `/api/payments/methods` and:
 *   • 0 active → renders nothing (calling page shows an error)
 *   • 1 active → renders a compact "Processed by X · [wallets]" info strip
 *   • 2+ active → renders side-by-side selectable cards
 *
 * Adding a new gateway is zero frontend work — just register it in the backend
 * `PAYMENT_PROVIDERS` registry.
 *
 * Props:
 *   value: currently-selected provider id
 *   onChange(providerId, provider): fired on selection
 *   onLoad({providers, default}): optional, fired once the list is loaded
 */
export default function PaymentMethodPicker({ value, onChange, onLoad, dataTestidPrefix = "pay" }) {
  const { t } = useTranslation();
  const [providers, setProviders] = useState(null); // null=loading, []=empty
  const [defaultId, setDefaultId] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get("/payments/methods").then((r) => {
      if (!alive) return;
      const list = r.data?.providers || [];
      setProviders(list);
      setDefaultId(r.data?.default || (list[0]?.id ?? null));
      onLoad?.(r.data || {});
    }).catch(() => alive && setProviders([]));
    return () => { alive = false; };
  }, []);

  // Auto-select the default once the list loads.
  useEffect(() => {
    if (providers && providers.length && !value && defaultId) {
      onChange?.(defaultId, providers.find((p) => p.id === defaultId));
    }
  }, [providers, defaultId]);

  const selected = useMemo(
    () => providers?.find((p) => p.id === value) || null,
    [providers, value],
  );

  if (providers === null) {
    return <div data-testid={`${dataTestidPrefix}-picker-loading`} className="text-xs font-mono text-zinc-400">{t("common.loading")}</div>;
  }
  if (providers.length === 0) return null;

  // ---------- SINGLE PROVIDER — compact info strip ----------
  if (providers.length === 1) {
    const p = providers[0];
    return (
      <div data-testid={`${dataTestidPrefix}-picker-single`} className="border border-zinc-200 bg-[#FAFAFA] px-5 py-4 flex flex-wrap items-center gap-x-6 gap-y-3">
        <div className="flex items-center gap-2 text-xs font-mono text-zinc-600">
          <ShieldCheck size={14} />
          <span>{t("paymentPicker.processedBy")}</span>
          <span className="text-zinc-950 font-semibold">{p.name}</span>
          <span className="text-zinc-400">·</span>
          <span>{p.region_label}</span>
        </div>
        <WalletBadges wallets={p.wallets} testid={`${dataTestidPrefix}-picker-wallets`} />
      </div>
    );
  }

  // ---------- MULTI PROVIDER — selectable cards ----------
  return (
    <div data-testid={`${dataTestidPrefix}-picker`} className="space-y-3">
      <div className="overline text-zinc-950">{t("paymentPicker.title")}</div>
      <div className="grid sm:grid-cols-2 gap-3">
        {providers.map((p) => {
          const isSel = p.id === value;
          const isDefault = p.id === defaultId;
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => onChange?.(p.id, p)}
              data-testid={`${dataTestidPrefix}-picker-${p.id}`}
              data-selected={isSel ? "true" : "false"}
              className={`text-start border-2 p-4 transition-colors relative ${isSel ? "border-blue-700 bg-blue-50/40" : "border-zinc-200 hover:border-zinc-950 bg-white"}`}
            >
              <div className="flex items-start justify-between gap-3 mb-2">
                <div>
                  <div className="font-display font-bold text-lg tracking-tight leading-tight">{p.name}</div>
                  <div className="text-[11px] font-mono text-zinc-500 mt-0.5">{p.region_label}</div>
                </div>
                {isDefault && (
                  <span className="text-[9px] font-mono uppercase tracking-[0.12em] px-1.5 py-0.5 bg-zinc-950 text-white leading-none whitespace-nowrap">
                    {t("paymentPicker.recommended")}
                  </span>
                )}
              </div>
              <div className="text-xs text-zinc-600 leading-snug mb-3">{p.description}</div>
              <WalletBadges wallets={p.wallets} testid={`${dataTestidPrefix}-picker-${p.id}-wallets`} />
              {isSel && (
                <div className="absolute top-2 end-2 text-blue-700">
                  <CheckCircle size={16} weight="fill" />
                </div>
              )}
            </button>
          );
        })}
      </div>
      {selected && (
        <div className="flex items-center gap-2 text-[11px] font-mono text-zinc-500 pt-1">
          <ShieldCheck size={12} />
          <span>{t("paymentPicker.securedNote", { provider: selected.name })}</span>
        </div>
      )}
    </div>
  );
}

function WalletBadges({ wallets, testid }) {
  if (!wallets?.length) return null;
  return (
    <div data-testid={testid} className="flex flex-wrap gap-1.5">
      {wallets.map((w) => (
        <span
          key={w}
          className="text-[10px] font-mono uppercase tracking-[0.08em] px-1.5 py-0.5 border border-zinc-300 text-zinc-700 bg-white"
        >
          {w}
        </span>
      ))}
    </div>
  );
}
